import { useEffect, useMemo, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { ErrorState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { todayIST, addDays, weekdayMon0, istDateOf, fmtTimeIST, fmtDayLabel } from "@/lib/dateRange";
import { ChevronLeft, ChevronRight, CheckCircle2, Circle } from "lucide-react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function monthGrid(month) {
  const first = `${month}-01`;
  const start = addDays(first, -weekdayMon0(first));
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

function shiftMonth(month, n) {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 7);
}

function DayItems({ day, items }) {
  return (
    <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-4" data-testid="calendar-day-panel">
      <h4 className="font-semibold text-slate-900 mb-3">{fmtDayLabel(day)}</h4>
      {items.length === 0 ? <p className="text-sm text-slate-400">Nothing scheduled or logged on this day.</p> : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={`${it.kind}-${it.id}`} className="flex items-start gap-3 text-sm" data-testid={`calendar-item-${it.id}`}>
              {it.kind === "task" ? (it.completed ? <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" /> : <Circle className="h-4 w-4 text-amber-500 mt-0.5" />) : <span className="mt-1.5 h-2 w-2 rounded-full bg-teal shrink-0" />}
              <div className="min-w-0">
                <div className="font-medium text-slate-800">{it.kind === "task" ? `Follow-up: ${it.title}` : it.type}{it.lead_name ? <span className="font-normal text-slate-500"> · {it.lead_name}</span> : null}</div>
                <div className="text-xs text-slate-400">{it.kind === "task" ? "Due" : fmtTimeIST(it.when)} · {it.user_name || "—"}{it.outcome ? ` · ${it.outcome}` : ""}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ActivityCalendar({ salesperson, refreshKey }) {
  const today = todayIST();
  const [month, setMonth] = useState(today.slice(0, 7));
  const [selected, setSelected] = useState(today);
  const [data, setData] = useState({ activities: [], tasks: [] });
  const [error, setError] = useState("");
  const days = useMemo(() => monthGrid(month), [month]);

  const load = useCallback(async () => {
    setError("");
    try {
      const params = { start: days[0], end: days[41], ...(salesperson ? { salesperson } : {}) };
      const { data } = await api.get("/activities/calendar", { params });
      setData(data);
    } catch (e) { setError(apiError(e)); }
  }, [days, salesperson]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const byDay = useMemo(() => {
    const map = {};
    data.activities.forEach((a) => (map[istDateOf(a.when)] ||= []).push({ ...a, kind: "activity" }));
    data.tasks.forEach((t) => (map[String(t.due_date).slice(0, 10)] ||= []).push({ ...t, kind: "task" }));
    return map;
  }, [data]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  const label = new Date(`${month}-01T00:00:00Z`).toLocaleDateString("en-IN", { timeZone: "UTC", month: "long", year: "numeric" });

  return (
    <div className="grid lg:grid-cols-[1fr_20rem] gap-4" data-testid="activity-calendar-view">
      <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-3 sm:p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900" data-testid="calendar-month-label">{label}</h3>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" className="h-9" onClick={() => { setMonth(today.slice(0, 7)); setSelected(today); }} data-testid="calendar-today">Today</Button>
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase text-slate-400 mb-1">{WEEKDAYS.map((w) => <div key={w}>{w}</div>)}</div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d) => {
            const items = byDay[d] || [];
            const inMonth = d.startsWith(month);
            return (
              <button key={d} onClick={() => setSelected(d)} data-testid={`calendar-day-${d}`}
                className={cn("min-h-[3.25rem] sm:min-h-[5.5rem] rounded-lg border p-1 sm:p-1.5 text-left transition-colors duration-150",
                  inMonth ? "bg-white" : "bg-slate-50 text-slate-300", selected === d ? "border-teal ring-2 ring-teal/20" : "border-slate-100 hover:border-teal/40")}>
                <div className={cn("text-xs font-semibold", d === today ? "inline-grid place-items-center h-5 w-5 rounded-full bg-teal text-white" : inMonth ? "text-slate-600" : "")}>{Number(d.slice(8))}</div>
                <div className="hidden sm:block mt-1 space-y-0.5">
                  {items.slice(0, 2).map((it) => (
                    <div key={`${it.kind}-${it.id}`} className={cn("truncate rounded px-1 text-[10px] font-medium", it.kind === "task" ? "bg-amber-50 text-amber-700" : "bg-teal/10 text-teal")}>{it.kind === "task" ? it.title : it.type}</div>
                  ))}
                  {items.length > 2 && <div className="text-[10px] text-slate-400">+{items.length - 2} more</div>}
                </div>
                {items.length > 0 && <div className="sm:hidden mt-1 flex gap-0.5">{items.slice(0, 3).map((it) => <span key={`${it.kind}-${it.id}`} className={cn("h-1.5 w-1.5 rounded-full", it.kind === "task" ? "bg-amber-500" : "bg-teal")} />)}</div>}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-teal" /> Activity</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /> Follow-up</span>
        </div>
      </div>
      <DayItems day={selected} items={byDay[selected] || []} />
    </div>
  );
}
